// src/Settings/DatabaseAdvisor/index.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import SearchIcon from '@material-ui/icons/Search';
import Content from '../Content';
import StatusTooltip from './StatusTooltip';
import { getService } from 'reducers/service';
import { handleError } from 'reducers/ErrorReducer';
import { notify } from 'reducers/NotifierReducer';
import {
  ADVISOR_TABS,
  FK_FILTER_OPTIONS,
  FK_STATUS_DESCRIPTIONS,
  INDEX_FILTER_OPTIONS,
  INDEX_STATUS_DESCRIPTIONS,
  ROW_STATUS_COLORS,
  SEVERITY_COLORS,
  SEVERITY_LABELS,
  SUMMARY_BADGE_DESCRIPTIONS,
  TABLE_FILTER_OPTIONS,
} from './constants';
import {
  filterFkFindings,
  filterIndexFindings,
  filterTables,
  formatCardSubtitle,
  getFkFindingStatus,
  getIndexFindingStatus,
  getRowStatus,
  pillStyle,
  searchFkFindings,
  searchIndexFindings,
  searchTables,
} from './utils';

const ROW_STATUS_ICONS = {
  missing: CancelRoundedIcon,
  critical: WarningRoundedIcon,
  error: WarningRoundedIcon,
  warning: WarningRoundedIcon,
  info: WarningRoundedIcon,
  healthy: CheckCircleRoundedIcon,
};

function DatabaseAdvisor(props) {
  const dbAdvisorService = getService('db-advisor');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('tables');
  const [filter, setFilter] = useState('issues');
  const [indexFilter, setIndexFilter] = useState('missing');
  const [fkFilter, setFkFilter] = useState('missing');
  const [tableSearch, setTableSearch] = useState('');
  const [indexSearch, setIndexSearch] = useState('');
  const [fkSearch, setFkSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [pendingScrollKey, setPendingScrollKey] = useState(null);
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [confirmFix, setConfirmFix] = useState(null);
  const [applyingKeys, setApplyingKeys] = useState(new Set());
  const [viewScriptDdl, setViewScriptDdl] = useState(null);
  const cardRefs = useRef({});

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dbAdvisorService.find();
      setData(result);
    } catch (err) {
      const cause = err.data?.cause;
      props.handleError(
        err,
        `Error loading the database advisor report.${cause ? ` (${cause})` : ''}`
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleExpanded = key =>
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const tables = data?.tables ?? [];
  const indexFindings = data?.indexFindings ?? [];
  const fkFindings = data?.foreignKeyFindings ?? [];
  const summary = data?.summary ?? {};
  const visibleTables = searchTables(filterTables(tables, filter), tableSearch);
  const visibleIndexFindings = searchIndexFindings(
    filterIndexFindings(indexFindings, indexFilter),
    indexSearch
  );
  const visibleFkFindings = searchFkFindings(filterFkFindings(fkFindings, fkFilter), fkSearch);

  useEffect(() => {
    if (!pendingScrollKey) return;
    const node = cardRefs.current[pendingScrollKey];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedKey(pendingScrollKey);
    setPendingScrollKey(null);
    const timeoutId = setTimeout(() => setHighlightedKey(null), 1500);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [pendingScrollKey, visibleTables]);

  const jumpToFirstTable = predicate => {
    const target = tables.find(predicate);
    if (!target) return;
    const key = `${target.tableName}-${target.modelName}`;
    setFilter('issues');
    setExpanded(previous => new Set(previous).add(key));
    setPendingScrollKey(key);
  };

  const jumpToMissingTable = () => jumpToFirstTable(table => !table.exists);
  const jumpToSeverity = severity =>
    jumpToFirstTable(table => table.findings.some(finding => finding.severity === severity));

  const copyScript = ddl => {
    if (navigator.clipboard) navigator.clipboard.writeText(ddl);
    props.notify('Copied to clipboard.', 'success');
  };

  const openConfirmFix = (tableName, ddl, findingKey) =>
    setConfirmFix({ tableName, ddl, findingKey });

  const closeConfirmFix = () => setConfirmFix(null);

  const closeViewScript = () => setViewScriptDdl(null);

  const applyFix = async () => {
    if (!confirmFix) return;
    const { tableName, ddl, findingKey } = confirmFix;

    setApplyingKeys(previous => new Set(previous).add(findingKey));
    try {
      await dbAdvisorService.create({ ddl, tableName });
      props.notify('Fix applied successfully — refreshing…', 'success');
      setConfirmFix(null);
      loadReport();
    } catch (err) {
      const cause = err.data?.cause;
      props.handleError(err, `Error applying fix.${cause ? ` (${cause})` : ''}`);
    } finally {
      setApplyingKeys(previous => {
        const next = new Set(previous);
        next.delete(findingKey);
        return next;
      });
    }
  };

  const missingIndexCount = indexFindings.filter(item => !item.exists).length;
  const missingFkCount = fkFindings.filter(item => !item.exists).length;

  return (
    <Content>
      <div className="db-advisor-wrap">
        <div className="db-advisor-header">
          <div>
            <Typography className="db-advisor-header-title">Database Advisor</Typography>
            <div className="db-advisor-header-subtitle">
              Compares every registered Sequelize model against the live database schema — tables,
              columns, indexes, and foreign keys.
            </div>
          </div>
          <Button
            variant="outlined"
            className="db-advisor-refresh-button"
            startIcon={<RefreshIcon />}
            onClick={loadReport}
            disabled={loading}>
            {loading ? 'Checking…' : 'Refresh'}
          </Button>
        </div>

        {loading && !data && (
          <div className="db-advisor-loading-wrap">
            <CircularProgress />
          </div>
        )}

        {data && (
          <>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              indicatorColor="primary"
              textColor="primary"
              className="db-advisor-tabs">
              {ADVISOR_TABS.map(tab => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>

            {activeTab === 'tables' && (
              <div className="db-advisor-tab-content">
                <div className="db-advisor-top-row">
                  <div className="db-advisor-badge-group">
                    <span className="db-advisor-badge-group-label">Tables</span>
                    {summary.missingTables > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.critical}
                        title={SUMMARY_BADGE_DESCRIPTIONS.missingTables}>
                        <span
                          className="db-advisor-clickable-badge"
                          style={pillStyle(SEVERITY_COLORS.critical)}
                          onClick={jumpToMissingTable}>
                          {summary.missingTables} missing
                        </span>
                      </StatusTooltip>
                    )}
                    {!summary.missingTables && (
                      <span style={pillStyle(ROW_STATUS_COLORS.healthy)}>All tables modeled</span>
                    )}
                  </div>

                  <div className="db-advisor-badge-divider" />

                  <div className="db-advisor-badge-group">
                    <span className="db-advisor-badge-group-label">Findings</span>
                    {summary.critical > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.critical}
                        title={SUMMARY_BADGE_DESCRIPTIONS.critical}>
                        <span
                          className="db-advisor-clickable-badge"
                          style={pillStyle(SEVERITY_COLORS.critical)}
                          onClick={() => jumpToSeverity('critical')}>
                          {summary.critical} critical
                        </span>
                      </StatusTooltip>
                    )}
                    {summary.errors > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.error}
                        title={SUMMARY_BADGE_DESCRIPTIONS.errors}>
                        <span
                          className="db-advisor-clickable-badge"
                          style={pillStyle(SEVERITY_COLORS.error)}
                          onClick={() => jumpToSeverity('error')}>
                          {summary.errors} errors
                        </span>
                      </StatusTooltip>
                    )}
                    {summary.warnings > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.warning}
                        title={SUMMARY_BADGE_DESCRIPTIONS.warnings}>
                        <span
                          className="db-advisor-clickable-badge"
                          style={pillStyle(SEVERITY_COLORS.warning)}
                          onClick={() => jumpToSeverity('warning')}>
                          {summary.warnings} warnings
                        </span>
                      </StatusTooltip>
                    )}
                    {summary.info > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.info}
                        title={SUMMARY_BADGE_DESCRIPTIONS.info}>
                        <span
                          className="db-advisor-clickable-badge"
                          style={pillStyle(SEVERITY_COLORS.info)}
                          onClick={() => jumpToSeverity('info')}>
                          {summary.info} info
                        </span>
                      </StatusTooltip>
                    )}
                    {!summary.critical && !summary.errors && !summary.warnings && !summary.info && (
                      <span style={pillStyle(ROW_STATUS_COLORS.healthy)}>No findings</span>
                    )}
                  </div>

                  <ButtonGroup size="small" className="db-advisor-filter-group">
                    {TABLE_FILTER_OPTIONS.map(option => (
                      <Button
                        key={option.value}
                        variant={filter === option.value ? 'contained' : 'outlined'}
                        color={filter === option.value ? 'primary' : 'default'}
                        onClick={() => setFilter(option.value)}>
                        {option.label} ({filterTables(tables, option.value).length})
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                <TextField
                  className="db-advisor-search-box"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Search by table or model name…"
                  value={tableSearch}
                  onChange={event => setTableSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {visibleTables.length === 0 && (
                  <div className="db-advisor-empty-state">
                    {tableSearch
                      ? 'No tables match this search.'
                      : filter === 'issues'
                      ? 'No issues found — the database matches every registered model.'
                      : 'No tables match this filter.'}
                  </div>
                )}

                <div className="db-advisor-scroll-box">
                  <div className="db-advisor-table-list">
                    {visibleTables.map(table => {
                      const key = `${table.tableName}-${table.modelName}`;
                      const status = getRowStatus(table);
                      const statusColor = ROW_STATUS_COLORS[status];
                      const StatusIcon = ROW_STATUS_ICONS[status];
                      const isOpen = expanded.has(key);

                      return (
                        <div
                          key={key}
                          ref={el => {
                            cardRefs.current[key] = el;
                          }}
                          className={`db-advisor-card ${highlightedKey === key ? 'db-advisor-highlighted-card' : ''}`}
                          style={{ border: `1px solid ${statusColor}55` }}>
                          <div
                            className="db-advisor-card-header"
                            style={{ background: `${statusColor}0d` }}
                            onClick={() => toggleExpanded(key)}>
                            <StatusIcon
                              style={{ color: statusColor, fontSize: 20, flexShrink: 0 }}
                            />
                            <div className="db-advisor-card-body">
                              <div className="db-advisor-card-title-row">
                                <span className="db-advisor-card-table-name">{table.tableName}</span>
                                <span className="db-advisor-card-model-name">{table.modelName}</span>
                                {table.sharedModelNames.length > 0 && (
                                  <Tooltip
                                    title={`Also targeted by: ${table.sharedModelNames.join(
                                      ', '
                                    )}`}>
                                    <span style={pillStyle('#607d8b')}>Shared table</span>
                                  </Tooltip>
                                )}
                              </div>
                              <div className="db-advisor-card-subtitle">
                                {formatCardSubtitle(table)}
                              </div>
                            </div>
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </div>

                          {isOpen && (
                            <div className="db-advisor-card-detail">
                              {table.findings.length === 0 && (
                                <Typography
                                  variant="body2"
                                  style={{ color: ROW_STATUS_COLORS.healthy, fontWeight: 600 }}>
                                  All columns and indexes match the model.
                                </Typography>
                              )}
                              {table.findings.map((finding, index) => {
                                const findingKey = `${key}-${finding.type}-${finding.column ||
                                  finding.index ||
                                  index}`;
                                const isApplying = applyingKeys.has(findingKey);

                                return (
                                  <div
                                    key={findingKey}
                                    className="db-advisor-finding"
                                    style={{
                                      borderLeftColor: SEVERITY_COLORS[finding.severity],
                                      background: `${SEVERITY_COLORS[finding.severity]}14`,
                                    }}>
                                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                                      {SEVERITY_LABELS[finding.severity]}: {finding.message}
                                    </Typography>
                                    {finding.comment && (
                                      <Typography variant="caption" color="textSecondary">
                                        {finding.comment}
                                      </Typography>
                                    )}
                                    {finding.fixDdl && (
                                      <>
                                        <code className="db-advisor-ddl-block">{finding.fixDdl}</code>
                                        <div className="db-advisor-finding-actions">
                                          {finding.fixable && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="primary"
                                              startIcon={<PlayArrowIcon />}
                                              disabled={isApplying}
                                              onClick={() =>
                                                openConfirmFix(
                                                  table.tableName,
                                                  finding.fixDdl,
                                                  findingKey
                                                )
                                              }>
                                              {isApplying ? 'Applying…' : 'Apply Fix'}
                                            </Button>
                                          )}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<FileCopyOutlinedIcon />}
                                            onClick={() => copyScript(finding.fixDdl)}>
                                            Copy Script
                                          </Button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'indexes' && (
              <div className="db-advisor-tab-content">
                <div className="db-advisor-top-row">
                  <div className="db-advisor-indexes-title" style={{ marginBottom: 0 }}>
                    <span>Performance Indexes</span>
                    {missingIndexCount > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.warning}
                        title={INDEX_STATUS_DESCRIPTIONS.missing}>
                        <span style={pillStyle(SEVERITY_COLORS.warning)}>
                          {missingIndexCount} missing
                        </span>
                      </StatusTooltip>
                    )}
                    {missingIndexCount === 0 && (
                      <StatusTooltip
                        color={ROW_STATUS_COLORS.healthy}
                        title={INDEX_STATUS_DESCRIPTIONS.healthy}>
                        <span style={pillStyle(ROW_STATUS_COLORS.healthy)}>All present</span>
                      </StatusTooltip>
                    )}
                  </div>

                  <ButtonGroup size="small" className="db-advisor-filter-group">
                    {INDEX_FILTER_OPTIONS.map(option => (
                      <Button
                        key={option.value}
                        variant={indexFilter === option.value ? 'contained' : 'outlined'}
                        color={indexFilter === option.value ? 'primary' : 'default'}
                        onClick={() => setIndexFilter(option.value)}>
                        {option.label} ({filterIndexFindings(indexFindings, option.value).length})
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                <TextField
                  className="db-advisor-search-box"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Search by index or table name…"
                  value={indexSearch}
                  onChange={event => setIndexSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {visibleIndexFindings.length === 0 && (
                  <div className="db-advisor-empty-state">
                    {indexSearch
                      ? 'No indexes match this search.'
                      : 'No indexes match this filter.'}
                  </div>
                )}

                <div className="db-advisor-scroll-box">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Index', 'Table', 'Description', 'Status', 'Actions'].map(heading => (
                          <TableCell key={heading} className="db-advisor-table-head-cell">
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleIndexFindings.map(item => {
                        const status = getIndexFindingStatus(item);
                        const statusColor =
                          status === 'present'
                            ? ROW_STATUS_COLORS.healthy
                            : SEVERITY_COLORS.warning;
                        const findingKey = `index-${item.tableName}-${item.name}`;
                        const isApplying = applyingKeys.has(findingKey);

                        return (
                          <TableRow key={`${item.tableName}-${item.name}`}>
                            <TableCell className="db-advisor-mono-cell" style={{ fontWeight: 700 }}>
                              {item.name}
                            </TableCell>
                            <TableCell className="db-advisor-mono-cell">{item.tableName}</TableCell>
                            <TableCell style={{ fontSize: 12 }}>{item.fields.join(', ')}</TableCell>
                            <TableCell>
                              <StatusTooltip
                                color={statusColor}
                                title={INDEX_STATUS_DESCRIPTIONS[status]}>
                                <span style={pillStyle(statusColor)}>
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                              </StatusTooltip>
                            </TableCell>
                            <TableCell>
                              {item.fixDdl && (
                                <div className="db-advisor-finding-actions" style={{ marginTop: 0 }}>
                                  {item.fixable && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<PlayArrowIcon />}
                                      disabled={isApplying}
                                      onClick={() =>
                                        openConfirmFix(item.tableName, item.fixDdl, findingKey)
                                      }>
                                      {isApplying ? 'Applying…' : 'Apply Fix'}
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityOutlinedIcon />}
                                    onClick={() => setViewScriptDdl(item.fixDdl)}>
                                    View Script
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {activeTab === 'foreignKeys' && (
              <div className="db-advisor-tab-content">
                <div className="db-advisor-top-row">
                  <div className="db-advisor-indexes-title" style={{ marginBottom: 0 }}>
                    <span>Foreign Keys</span>
                    {missingFkCount > 0 && (
                      <StatusTooltip
                        color={SEVERITY_COLORS.warning}
                        title={FK_STATUS_DESCRIPTIONS.missing}>
                        <span style={pillStyle(SEVERITY_COLORS.warning)}>
                          {missingFkCount} missing
                        </span>
                      </StatusTooltip>
                    )}
                    {missingFkCount === 0 && (
                      <StatusTooltip
                        color={ROW_STATUS_COLORS.healthy}
                        title={FK_STATUS_DESCRIPTIONS.healthy}>
                        <span style={pillStyle(ROW_STATUS_COLORS.healthy)}>All present</span>
                      </StatusTooltip>
                    )}
                  </div>

                  <ButtonGroup size="small" className="db-advisor-filter-group">
                    {FK_FILTER_OPTIONS.map(option => (
                      <Button
                        key={option.value}
                        variant={fkFilter === option.value ? 'contained' : 'outlined'}
                        color={fkFilter === option.value ? 'primary' : 'default'}
                        onClick={() => setFkFilter(option.value)}>
                        {option.label} ({filterFkFindings(fkFindings, option.value).length})
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                <TextField
                  className="db-advisor-search-box"
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Search by column, table, or referenced table…"
                  value={fkSearch}
                  onChange={event => setFkSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {visibleFkFindings.length === 0 && (
                  <div className="db-advisor-empty-state">
                    {fkSearch
                      ? 'No foreign keys match this search.'
                      : 'No foreign keys match this filter.'}
                  </div>
                )}

                <div className="db-advisor-scroll-box">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Column', 'Table', 'References', 'Status', 'Actions'].map(heading => (
                          <TableCell key={heading} className="db-advisor-table-head-cell">
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleFkFindings.map(item => {
                        const status = getFkFindingStatus(item);
                        const statusColor =
                          status === 'present'
                            ? ROW_STATUS_COLORS.healthy
                            : SEVERITY_COLORS.warning;
                        const findingKey = `fk-${item.tableName}-${item.column}`;
                        const isApplying = applyingKeys.has(findingKey);

                        return (
                          <TableRow key={`${item.tableName}-${item.column}`}>
                            <TableCell className="db-advisor-mono-cell" style={{ fontWeight: 700 }}>
                              {item.column}
                            </TableCell>
                            <TableCell className="db-advisor-mono-cell">{item.tableName}</TableCell>
                            <TableCell className="db-advisor-mono-cell" style={{ fontSize: 11 }}>
                              {item.referencedTable
                                ? `${item.referencedTable}.${item.referencedColumn}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <StatusTooltip
                                color={statusColor}
                                title={item.message || FK_STATUS_DESCRIPTIONS[status]}>
                                <span style={pillStyle(statusColor)}>
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                              </StatusTooltip>
                            </TableCell>
                            <TableCell>
                              {item.fixDdl && (
                                <div className="db-advisor-finding-actions" style={{ marginTop: 0 }}>
                                  {item.fixable && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<PlayArrowIcon />}
                                      disabled={isApplying}
                                      onClick={() =>
                                        openConfirmFix(item.tableName, item.fixDdl, findingKey)
                                      }>
                                      {isApplying ? 'Applying…' : 'Apply Fix'}
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VisibilityOutlinedIcon />}
                                    onClick={() => setViewScriptDdl(item.fixDdl)}>
                                    View Script
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!confirmFix} onClose={closeConfirmFix} maxWidth="sm" fullWidth>
        <DialogTitle>Apply fix to {confirmFix?.tableName}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 10 }}>
            This will run the following statement directly against the database. This cannot be
            undone automatically.
          </Typography>
          <code className="db-advisor-dialog-ddl">{confirmFix?.ddl}</code>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmFix}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={confirmFix && applyingKeys.has(confirmFix.findingKey)}
            onClick={applyFix}>
            {confirmFix && applyingKeys.has(confirmFix.findingKey)
              ? 'Applying…'
              : 'Confirm & Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewScriptDdl} onClose={closeViewScript} maxWidth="sm" fullWidth>
        <DialogTitle>Script</DialogTitle>
        <DialogContent>
          <code className="db-advisor-dialog-ddl">{viewScriptDdl}</code>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewScript}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileCopyOutlinedIcon />}
            onClick={() => copyScript(viewScriptDdl)}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
}

export default connect(null, { handleError, notify })(DatabaseAdvisor);
